import React, { useState } from "react";
import styled from "styled-components";
import { intersection } from "lodash";
import { AvatarProps } from "@sonder/ui/source/styled/components/Avatar";
import { Dropdown } from "@sonder/ui/source/styled/components/Dropdown";
import { MultiAvatar } from "@sonder/ui/source/styled/components/MultiAvatar";
import {
  FilterButtonList,
  FilterDropdownContent,
  FilterTextArea,
} from "@sonder/ui/source/styled/fragments/GenericFilters";
import { colors } from "@sonder/ui/source/styled/theme";
import { useStore as useAuthStore } from "auth/store";
import {
  TagTypes,
  TagsToIcons,
  TomTags,
  useGetTagsQuery,
} from "core/queries/GetTags";
import { TagData } from "core/queries/GetTeam";
import { SquadManagementGroupItem } from "team/components/SquadManagementGroupItem";
import { DispatchRoleName } from "typings/globalGQLTypes";

const Trigger = styled.div`
  &:hover {
    color: ${colors.black};
  }
`;

interface UserSquadPickerProps {
  tags: TagData[];
  userId: string;
}

const getAvatars = (tags: TagData[]) => {
  return tags.reduce((acc, currentTag) => {
    const Icon = TagsToIcons(currentTag.name);
    if (!Icon || currentTag.tagType !== TagTypes.Squad) {
      return acc;
    }

    return acc.concat({
      name: currentTag.name,
      svg: <Icon />,
    });
  }, [] as AvatarProps[]);
};

const isChecked = (tags: TagData[], squad: TomTags) => {
  return tags.map(tag => tag.id).includes(squad.id);
};

export const UserSquadPicker = ({ tags, userId }: UserSquadPickerProps) => {
  const { state: authState } = useAuthStore();
  const [search, setSearch] = useState("");

  const avatars = getAvatars(tags);

  const { data } = useGetTagsQuery({
    variables: {
      tagType: TagTypes.Squad,
    },
  });

  const squadTags = data?.tomTags || [];
  const roles = authState.user?.roles || [];
  if (
    intersection(roles, [
      DispatchRoleName.tom_manager,
      DispatchRoleName.tom_team_lead,
    ]).length === 0
  ) {
    return avatars.length > 0 ? (
      <MultiAvatar avatars={avatars} maxLength={4} />
    ) : null;
  }

  const filteredSquads =
    search !== ""
      ? squadTags.filter(tag =>
          tag.name.toLowerCase().includes(search.toLowerCase()),
        )
      : squadTags;

  const renderSquad = (squad: TomTags) => {
    return (
      <SquadManagementGroupItem
        isChecked={isChecked(tags, squad)}
        key={squad.id}
        squad={squad}
        userId={userId}
      />
    );
  };

  return (
    <Dropdown
      trigger={
        avatars.length > 0 ? (
          <MultiAvatar avatars={avatars} maxLength={4} />
        ) : (
          <Trigger>Set Squads</Trigger>
        )
      }
    >
      {({ isOpen: isPortalOpen }) =>
        isPortalOpen && (
          <FilterDropdownContent align="left">
            <FilterTextArea
              autoFocus
              placeholder="Search squad types"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <FilterButtonList>
              {filteredSquads.map(renderSquad)}
            </FilterButtonList>
          </FilterDropdownContent>
        )
      }
    </Dropdown>
  );
};
